/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import classnames from 'classnames';
import * as React from 'react';

import {
    IPriceEditorModeSelectionResources,
    PriceEditorModeSelectionComponent,
    PriceEditorModeSelectionState
} from './price-editor-mode-selection';

/**
 * Resources for price editor.
 */
export interface IPriceEditorResources extends IPriceEditorModeSelectionResources {
    priceEditorInputAriaLabel: string;
}

/**
 * The props required to render the component.
 */
export interface IPriceEditorProps {
    className: string;

    defaultAmount: number;

    resources: IPriceEditorResources;

    minimumValue?: number;
    maximumValue?: number;

    handleChange?(newValue: number): void;
}

/**
 * The state of the component.
 */
export interface IPriceEditorState {
    currentValue?: number;
    isInputFocused: boolean;
}

/**
 * Provides an input field which allows entering a price amount.
 */
export class PriceEditor extends React.PureComponent<IPriceEditorProps, IPriceEditorState> {
    private static readonly _precision = 2;

    private readonly _inputRef: React.RefObject<HTMLInputElement>;

    public constructor(props: IPriceEditorProps) {
        super(props);

        this._inputRef = React.createRef<HTMLInputElement>();

        let defaultValue = props.defaultAmount;

        if (!ObjectExtensions.isNullOrUndefined(this.props.minimumValue) && defaultValue < this.props.minimumValue) {
            defaultValue = this.props.minimumValue;
        } else if (!ObjectExtensions.isNullOrUndefined(this.props.maximumValue) && defaultValue > this.props.maximumValue) {
            defaultValue = this.props.maximumValue;
        }

        this.state = {
            currentValue: defaultValue,
            isInputFocused: false
        };
    }

    public render(): JSX.Element {
        const containerClassName = 'msc-price-editor';

        const hasMultipleModes = !ObjectExtensions.isNullOrUndefined(this.props.maximumValue);

        let mode = PriceEditorModeSelectionState.partial;

        if (!ObjectExtensions.isNullOrUndefined(this.props.maximumValue) && this.state.currentValue === this.props.maximumValue) {
            mode = PriceEditorModeSelectionState.full;
        }

        const formattedValue = this.state.currentValue?.toFixed(PriceEditor._precision) ?? '';
        const editedValue = this.state.currentValue ?? '';

        return (
            <div className={classnames(this.props.className, containerClassName)}>
                <input
                    className={classnames(`${this.props.className}__input`, `${containerClassName}__input`)}
                    type="number"
                    min={this.props.minimumValue}
                    max={this.props.maximumValue}
                    pattern="[0-9]*([.][0-9]{1,2})?"
                    value={this.state.isInputFocused ? editedValue : formattedValue}
                    onChange={this._handleChange}
                    aria-live="polite"
                    aria-label={`${this.props.resources.priceEditorInputAriaLabel}`}
                    role="spinbutton"
                    aria-valuemin={this.props.minimumValue}
                    aria-valuemax={this.props.maximumValue}
                    aria-valuenow={this.state.currentValue}
                    onFocus={this._handleInputFocus}
                    onBlur={this._handleInputBlur}
                    ref={this._inputRef}
                />
                {hasMultipleModes && (
                    <div className={classnames(`${this.props.className}__actions`, `${containerClassName}__actions`)}>
                        <PriceEditorModeSelectionComponent
                            key={mode}
                            className={`${this.props.className}__actions__price-editor-mode-selection`}
                            currentState={mode}
                            resources={this.props.resources}
                            onStateChanged={this._handleEditorModeStateChange}
                        />
                    </div>
                )}
            </div>
        );
    }

    /**
     * Handles input focus.
     */
    private readonly _handleInputFocus = () => {
        this.setState({ isInputFocused: true });
    };

    /**
     * Handles input blur.
     */
    private readonly _handleInputBlur = () => {
        this.setState({ isInputFocused: false });
    };

    /**
     * Handles editor mode state change.
     * @param newState - New selection mode state.
     */
    private readonly _handleEditorModeStateChange = (newState: PriceEditorModeSelectionState) => {
        if (newState === PriceEditorModeSelectionState.full) {
            this._updateAmount(this.props.maximumValue!);
        } else {
            this.setState({ currentValue: undefined });
            this._inputRef.current?.focus();
        }
    };

    /**
     * Update the price and calls corresponding callbacks.
     * @param newAmount - New price amount.
     */
    private readonly _updateAmount = (newAmount: number) => {
        this.setState({ currentValue: newAmount });

        if (this.props.handleChange) {
            this.props.handleChange(newAmount);
        }
    };

    /**
     * Handles value change.
     * @param event - React change event.
     */
    private readonly _handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        let currentValue = Number.parseFloat(event.target.value);

        if (Number.isNaN(currentValue)) {
            this.setState({ currentValue: undefined });
            return;
        }

        currentValue = +currentValue.toFixed(PriceEditor._precision);

        if (!ObjectExtensions.isNullOrUndefined(this.props.minimumValue) && currentValue < this.props.minimumValue) {
            this._updateAmount(this.props.minimumValue);
        } else if (!ObjectExtensions.isNullOrUndefined(this.props.maximumValue) && currentValue > this.props.maximumValue) {
            this._updateAmount(this.props.maximumValue);
        } else {
            this._updateAmount(currentValue);
        }
    };
}
